import React, { useCallback, useEffect, useState } from 'react';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_LOW
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLinkDialog } from './lib/linkDialog';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  ListNode
} from '@lexical/list';
import {
  $createQuoteNode,
  QuoteNode
} from '@lexical/rich-text';
import {
  $createCodeNode,
  CodeNode
} from '@lexical/code';
import { $setBlocksType } from '@lexical/selection';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import './ComposerToolbar.css';

/**
 * KAP-305 — formatting toolbar rendered above the V2 composer's
 * ContentEditable. Mirrors Slack's toolbar layout:
 *   B I U S | link | OL UL Quote | inline code | code block
 *
 * Selection-aware: each button reflects the format(s) present at the
 * current selection via `aria-pressed`. WCAG 2.5.5 24x24 hit targets
 * enforced in `ComposerToolbar.css`. Focus returns to the editor after
 * any button activation.
 */

function ToolbarButton({ label, shortcut, pressed, onActivate, children }) {
  const handleMouseDown = useCallback((event) => {
    // Prevent the editor from losing selection when the button is clicked.
    // Without this, clicking the toolbar moves focus away and the format
    // command fires against a collapsed selection at index 0.
    event.preventDefault();
  }, []);

  const handleClick = useCallback(
    (event) => {
      event.preventDefault();
      onActivate();
    },
    [onActivate]
  );

  return (
    <button
      type="button"
      className={`msg-composer-toolbar-btn${pressed ? ' is-pressed' : ''}`}
      aria-label={label}
      aria-pressed={pressed}
      title={shortcut ? `${label} (${shortcut})` : label}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="msg-composer-toolbar-divider" aria-hidden="true" />;
}

function ComposerToolbar({ visible }) {
  const [editor] = useLexicalComposerContext();
  const linkDialog = useLinkDialog();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isInlineCode, setIsInlineCode] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [blockType, setBlockType] = useState('paragraph'); // 'paragraph' | 'ul' | 'ol' | 'quote' | 'code'

  const refreshSelectionState = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    setIsBold(selection.hasFormat('bold'));
    setIsItalic(selection.hasFormat('italic'));
    setIsUnderline(selection.hasFormat('underline'));
    setIsStrikethrough(selection.hasFormat('strikethrough'));
    setIsInlineCode(selection.hasFormat('code'));

    const anchorNode = selection.anchor.getNode();
    const parents = anchorNode.getParents();
    const isInLink = [anchorNode, ...parents].some(
      (n) => typeof n.getType === 'function' && n.getType() === 'link'
    );
    setIsLink(isInLink);

    const codeAncestor = $getNearestNodeOfType(anchorNode, CodeNode);
    if (codeAncestor) {
      setBlockType('code');
      return;
    }
    const quoteAncestor = $getNearestNodeOfType(anchorNode, QuoteNode);
    if (quoteAncestor) {
      setBlockType('quote');
      return;
    }
    const listAncestor = $getNearestNodeOfType(anchorNode, ListNode);
    if (listAncestor && $isListNode(listAncestor)) {
      setBlockType(listAncestor.getListType() === 'number' ? 'ol' : 'ul');
      return;
    }
    setBlockType('paragraph');
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          refreshSelectionState();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          refreshSelectionState();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, refreshSelectionState]);

  const formatInline = useCallback(
    (format) => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
      editor.focus();
    },
    [editor]
  );

  const insertLink = useCallback(() => {
    // KAP-304 + post-user-smoke fix: dispatching TOGGLE_LINK_COMMAND with
    // an empty string used to create an empty-URL LinkNode (the popover
    // never listened for it). Open the dialog directly instead, capturing
    // the current selection's text so the Text field pre-populates.
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      editor.focus();
      return;
    }
    let selectedText = '';
    let selectionRect = null;
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selectedText = selection.getTextContent();
      }
    });
    const windowSelection = typeof window !== 'undefined' ? window.getSelection() : null;
    if (windowSelection && windowSelection.rangeCount > 0) {
      const rect = windowSelection.getRangeAt(0).getBoundingClientRect();
      if (rect && (rect.width || rect.height)) {
        selectionRect = { top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right, width: rect.width, height: rect.height };
      }
    }
    linkDialog.open({
      anchorRect: selectionRect,
      defaultUrl: /^https?:\/\//i.test(selectedText) ? selectedText : '',
      defaultText: /^https?:\/\//i.test(selectedText) ? '' : selectedText
    });
  }, [editor, isLink, linkDialog]);

  const toggleList = useCallback(
    (kind) => {
      const currentlyActive =
        (kind === 'ol' && blockType === 'ol') || (kind === 'ul' && blockType === 'ul');
      if (currentlyActive) {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      } else {
        editor.dispatchCommand(
          kind === 'ol' ? INSERT_ORDERED_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
          undefined
        );
      }
      editor.focus();
    },
    [editor, blockType]
  );

  const toggleQuote = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      $setBlocksType(
        selection,
        () => (blockType === 'quote' ? $createParagraphNode() : $createQuoteNode())
      );
    });
    editor.focus();
  }, [editor, blockType]);

  const toggleCodeBlock = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      $setBlocksType(
        selection,
        () => (blockType === 'code' ? $createParagraphNode() : $createCodeNode())
      );
    });
    editor.focus();
  }, [editor, blockType]);

  if (!visible) return null;

  return (
    <div
      className="msg-composer-toolbar"
      role="toolbar"
      aria-label="Message formatting"
    >
      <ToolbarButton
        label="Bold"
        shortcut="Cmd+B"
        pressed={isBold}
        onActivate={() => formatInline('bold')}
      >
        <span className="msg-composer-toolbar-icon" aria-hidden="true">B</span>
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        shortcut="Cmd+I"
        pressed={isItalic}
        onActivate={() => formatInline('italic')}
      >
        <span className="msg-composer-toolbar-icon msg-composer-toolbar-icon--italic" aria-hidden="true">
          I
        </span>
      </ToolbarButton>
      <ToolbarButton
        label="Underline"
        shortcut="Cmd+U"
        pressed={isUnderline}
        onActivate={() => formatInline('underline')}
      >
        <span className="msg-composer-toolbar-icon msg-composer-toolbar-icon--underline" aria-hidden="true">
          U
        </span>
      </ToolbarButton>
      <ToolbarButton
        label="Strikethrough"
        shortcut="Cmd+Shift+X"
        pressed={isStrikethrough}
        onActivate={() => formatInline('strikethrough')}
      >
        <span className="msg-composer-toolbar-icon msg-composer-toolbar-icon--strike" aria-hidden="true">
          S
        </span>
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="Link"
        shortcut="Cmd+K"
        pressed={isLink}
        onActivate={insertLink}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M10 14a4 4 0 015.66 0l3-3a4 4 0 00-5.66-5.66l-1.5 1.5M14 10a4 4 0 01-5.66 0l-3 3a4 4 0 005.66 5.66l1.5-1.5"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="Ordered list"
        pressed={blockType === 'ol'}
        onActivate={() => toggleList('ol')}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M4 6h1m-1 6h1m-1 6h1M9 6h11M9 12h11M9 18h11"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </ToolbarButton>
      <ToolbarButton
        label="Unordered list"
        pressed={blockType === 'ul'}
        onActivate={() => toggleList('ul')}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <circle cx="5" cy="6" r="1.2" fill="currentColor" />
          <circle cx="5" cy="12" r="1.2" fill="currentColor" />
          <circle cx="5" cy="18" r="1.2" fill="currentColor" />
          <path d="M9 6h11M9 12h11M9 18h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </ToolbarButton>
      <ToolbarButton label="Blockquote" pressed={blockType === 'quote'} onActivate={toggleQuote}>
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M5 7h4v4H7c0 2 1 3 3 3v2c-3 0-5-2-5-5V7zm9 0h4v4h-2c0 2 1 3 3 3v2c-3 0-5-2-5-5V7z"
            fill="currentColor"
          />
        </svg>
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="Inline code"
        shortcut="Cmd+E"
        pressed={isInlineCode}
        onActivate={() => formatInline('code')}
      >
        <span className="msg-composer-toolbar-icon msg-composer-toolbar-icon--mono" aria-hidden="true">
          {'< >'}
        </span>
      </ToolbarButton>
      <ToolbarButton
        label="Code block"
        pressed={blockType === 'code'}
        onActivate={toggleCodeBlock}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M8 7l-5 5 5 5M16 7l5 5-5 5"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </ToolbarButton>
    </div>
  );
}

export default ComposerToolbar;
